import { useMemo } from "react";
import type { FilterOperator } from "@/lib/filters";
import type { FilterEntityType } from "@/lib/filters/types";
import type { EntityType } from "@/types/entity";
import { useFieldDefinitions } from "./api/use-field-definitions";

/**
 * Supported scope values for the upcoming FilterBuilder.
 * "account" and "opportunity" intentionally use the **singular** form because
 * that is how the FilterBuilder API is documented in `campaign-filter-builder-plan.md`.
 */
export type FilterScope = Extract<
  FilterEntityType,
  "account" | "opportunity" | "campaign" | "touchpoint"
>;

import type { SelectOption } from "@/components/filter-popover";

export interface AvailableFilterField {
  id: string;
  label: string;
  dataType: string;
  validOperators: FilterOperator[];
  hasEnumValues: boolean;
  /** Can be string[] for simple values or SelectOption[] for labeled values */
  enumValues?: string[] | SelectOption[];
  isCustom: boolean;
}

/**
 * Transforms the raw `FieldDefinition` payload fetched from the server into the
 * lean shape required by the `FilterBuilder` UI component. The heavy lifting of
 * determining valid operators per data-type is already done on the server – we
 * simply expose the list verbatim.
 *
 * The hook automatically memoises the transformed result to avoid unnecessary
 * re-computations when React re-renders. Memoisation is *per scope* so a change
 * in the underlying server response correctly invalidates the cache.
 */
export function useAvailableFilterFields(scope: FilterScope) {
  // Map singular scope to the plural entity names understood by the API layer
  const scopeToEntity: Record<FilterScope, EntityType> = {
    account: "accounts",
    opportunity: "opportunities",
    campaign: "campaigns",
    touchpoint: "touchpoints",
  };
  const entity = scopeToEntity[scope];

  const { data = [], isLoading, error } = useFieldDefinitions(entity);

  // ----------------------------------------------------------------------------------
  // Memoised transformation → AvailableFilterField[]
  // ----------------------------------------------------------------------------------

  const availableFields = useMemo<AvailableFilterField[]>(() => {
    return data
      .filter(
        (fd) =>
          Array.isArray(fd.valid_operators) && fd.valid_operators.length > 0,
      )
      .map((fd) => ({
        id: fd.id,
        label: fd.display_name,
        dataType: fd.data_type,
        validOperators: fd.valid_operators,
        hasEnumValues: fd.has_enum_values,
        enumValues: fd.enum_values ?? undefined,
        isCustom: fd.is_custom,
      }));
  }, [data]);

  return {
    fields: availableFields,
    isLoading,
    error,
  };
}
